import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import { Button } from 'components/Form/Button/Button';

type Props = {
  focusOnly?: boolean;
  showWhenSingle?: boolean;
};

export const NavigationFooter = ({ focusOnly = false, showWhenSingle = false }: Props) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const currentItem = service.useCurrentItem();
  // Visibility is keyed on cycleable items (non-pending), NOT active items.
  // Active count is ack-aware in WebEDIErrorsService — using it would hide the
  // Prev/Next buttons when the cycle has multiple items but only one remains
  // unacked, blocking the user from navigating back to ack'd items.
  const numberOfCycleableItems = service.useNumberOfCycleableItems();

  const shouldShow = numberOfCycleableItems > 1 || (showWhenSingle && numberOfCycleableItems > 0);
  if (!shouldShow) {
    return null;
  }

  const movePrevious = () => {
    track('webedi:previous_error', { error: currentItem });
    if (focusOnly) {
      service.focusOnCurrentItem();
      return;
    }
    service.moveToPreviousItem();
  };

  const moveNext = () => {
    track('webedi:next_error', { error: currentItem });
    if (focusOnly) {
      service.focusOnCurrentItem();
      return;
    }
    service.moveToNextItem();
  };

  return (
    <>
      <Button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={movePrevious}
        variant="minimal"
        LeftIcon={ChevronLeftIcon}
        size="extra-small"
        analyticsId="webedi:previous_error"
      >
        {t('webedi:fieldAnnotation.previous')}
      </Button>
      <Button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={moveNext}
        variant="minimal"
        RightIcon={ChevronRightIcon}
        size="extra-small"
        analyticsId="webedi:next_error"
      >
        {t('webedi:fieldAnnotation.next')}
      </Button>
    </>
  );
};
